import axios from "axios";
import { useState, useEffect } from "react";
import { Alert, Container, TextField, Tooltip } from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import { Search } from "@mui/icons-material";
import LoadingPanel from "../../../components/common/LoadingPanel";

export default function AddOrEditContractor() {
  const {
    contractorModel,
    setContractorModel,
    locations,
    setCity,
    cities,
    config,
    CONTRACTOR_API,
  } = useGlobalContext();

  const handleChange = (e) => {
    setContractorModel({
      ...contractorModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleIdentificationTypeChange = (identificationType) => {
    if (identificationType) {
      setContractorModel({
        ...contractorModel,
        IdentificationType: identificationType,
      });
    }
  };

  const handleStateChange = (currentState) => {
    if (!currentState) {
      setContractorModel({
        ...contractorModel,
        StateId: 0,
        State: null,
        CityId: 0,
        City: null,
      });
      return;
    }

    setContractorModel({
      ...contractorModel,
      StateId: currentState.Id,
      State: currentState,
      CityId: 0,
      City: null,
    });
  };

  const handleCityChange = (currentCity) => {
    if (!currentCity) {
      setContractorModel({
        ...contractorModel,
        CityId: 0,
        City: null,
      });
      return;
    }

    setContractorModel({
      ...contractorModel,
      CityId: currentCity.Id,
      City: currentCity,
    });
  };

  const [states, setStates] = useState(null);
  const [citiesData, setCities] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState({ show: false });

  const validateStateChange = () => {
    if (contractorModel?.StateId > 0) {
      const selectedState = states?.find(
        (element) => element.Id === contractorModel?.StateId
      );
      if (selectedState) {
        setCity(selectedState.Code);
      }
    }
  };

  useEffect(() => {
    validateStateChange();
  }, [contractorModel?.StateId, states]);

  useEffect(() => {
    if (locations.Ok && locations.Data.length > 0) {
      setStates(locations.Data);
    }
  }, [locations]);

  useEffect(() => {
    if (cities?.Ok && cities.Data.length > 0) {
      setCities(cities.Data);
    }
  }, [cities]);

  const isSearchAvailable = () =>
    contractorModel?.Identification?.replaceAll(" ", "").length > 0;

  const getContractorInfoByIdentification = async () => {
    try {
      if (contractorModel?.Identification?.replaceAll(" ", "").length === 0) {
        return;
      }
      setShowWarning({ show: false });
      setIsLoading(true);
      const filteringConfig = {
        ...config,
        headers: {
          ...config.headers,
          Filter: JSON.stringify([
            {
              Name: "Identification",
              FriendlyName: localize.identification,
              FilterValue: { Value: contractorModel.Identification },
              Value: contractorModel.Identification,
            },
            {
              Name: "IdentificationType",
              FriendlyName: localize.identification_type,
              FilterValue: { Value: contractorModel.IdentificationType.Id },
              Value: contractorModel.IdentificationType.Id,
            },
          ]),
          pagination: 0,
        },
      };
      const data = await axios.get(CONTRACTOR_API, filteringConfig);

      if (data?.data?.Data && data?.data?.Data.length > 0) {
        setContractorModel({
          ...data?.data?.Data[0],
          IdentificationType: {
            Id: data?.data?.Data[0].IdentificationType,
            Name: data?.data?.Data[0].IdentificationType,
          },
        });

        setShowWarning({
          show: true,
          severity: "warning",
          message: localize.found_contractor_warning,
        });
        return;
      }

      setShowWarning({
        show: true,
        severity: "info",
        message: localize.no_data,
      });
    } catch (error) {
      setShowWarning({
        show: true,
        severity: "error",
        message: `${localize.error_unexpected}: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter") {
      getContractorInfoByIdentification();
    }
  };

  return (
    <div>
      <Container
        sx={{ paddingLeft: "0px !important", paddingRight: "0px !important" }}
        style={{ width: "100%" }}
      >
        <div className="w-full">
          <section className="flex justify-center content-center items-center ">
            {isLoading ? (
              <LoadingPanel />
            ) : (
              <section id="ReportData" className="w-full">
                <div className="flex gap-2 my-3 items-center">
                  <Combobox
                    isRequired
                    id="IdentificationType"
                    handleValueChange={handleIdentificationTypeChange}
                    fieldValue={contractorModel?.IdentificationType ?? null}
                    fieldOptions={[
                      {
                        Id: "Cédula de ciudadanía",
                        Name: "Cédula de ciudadanía",
                      },
                      { Id: "Nit", Name: "Nit" },
                      { Id: "Pasaporte", Name: "Pasaporte" },
                      {
                        Id: "Cédula de extranjería",
                        Name: "Cédula de extranjería",
                      },
                      { Id: "Otro", Name: "Otro" },
                    ]}
                    fieldLabel={localize.identification_type}
                  />
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="Identification"
                    value={contractorModel.Identification}
                    id="Identification"
                    label={localize.identification}
                    variant="outlined"
                    onChange={handleChange}
                    onKeyUp={onKeyUp}
                    autoComplete="off"
                  />

                  <Tooltip title={localize.contractor_search_tooltip}>
                    <button
                      disabled={!isSearchAvailable()}
                      style={{
                        color: isSearchAvailable() ? "#10366e" : "lightgray",
                      }}
                      onClick={getContractorInfoByIdentification}
                    >
                      <Search
                        sx={{
                          width: "24px",
                          height: "24px",
                        }}
                      />
                    </button>
                  </Tooltip>
                </div>

                {showWarning?.show && (
                  <Alert className="mb-3" severity={showWarning.severity}>
                    <p className="report_little_text">{showWarning.message}</p>
                  </Alert>
                )}

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Name"
                  id="Name"
                  value={contractorModel.Name}
                  label={localize.name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
                {/* States */}
                <div className="flex justify-between my-4">
                  <Combobox
                    isRequired
                    id="State"
                    handleValueChange={handleStateChange}
                    fieldValue={contractorModel?.State}
                    fieldOptions={states ?? []}
                    fieldLabel={localize.department}
                  />

                  {/* Cities */}
                  <div className="mx-2" />
                  <Combobox
                    isRequired
                    id="City"
                    handleValueChange={handleCityChange}
                    fieldValue={contractorModel?.City}
                    fieldOptions={
                      contractorModel?.State ? citiesData ?? [] : []
                    }
                    fieldLabel={localize.city}
                  />
                </div>
                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Address"
                  id="Address"
                  value={contractorModel.Address}
                  label={localize.address}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />

                <div className="flex justify-between my-4">
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="PhoneNumber"
                    id="PhoneNumber"
                    value={contractorModel.PhoneNumber}
                    label={localize.phone_number}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  <div className="mx-2" />
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="Email"
                    id="Email"
                    value={contractorModel.Email}
                    label={localize.email}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  value={contractorModel.ContactName}
                  name="ContactName"
                  id="ContactName"
                  label={localize.contact_name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
              </section>
            )}
          </section>
        </div>
      </Container>
    </div>
  );
}
